import { useEffect, useMemo, useRef } from "react";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { usePastMemberElectionRuns } from "../_hooks/usePastMemberElectionRuns";
import type { MemberElectionRunNode } from "../types";
import PastMemberElectionRunsTable from "./PastMemberElectionRunsTable";

function syncSelectedRunCache(
  cache: Record<string, MemberElectionRunNode>,
  runs: MemberElectionRunNode[],
  rowSelection: Record<string, boolean>
) {
  let changed = false;

  for (const run of runs) {
    if (rowSelection[run.id] && cache[run.id] !== run) {
      cache[run.id] = run;
      changed = true;
    }
  }

  for (const id of Object.keys(cache)) {
    if (!rowSelection[id]) {
      delete cache[id];
      changed = true;
    }
  }

  return changed;
}

export default function PastMemberElectionsContent() {
  const bulkSelection = useBulkSelection();
  const { rowSelection, rowSelectionCount, isSelectedOnly, deselectAll } = bulkSelection;
  const { runs, loading, showLoadError, createHeaderPagination, createFooterPagination } = usePastMemberElectionRuns();
  const cacheRef = useRef<Record<string, MemberElectionRunNode>>({});
  const selectedRunByIdRef = useRef<Record<string, MemberElectionRunNode>>({});

  const selectedRunById = useMemo(() => {
    const changed = syncSelectedRunCache(cacheRef.current, runs, rowSelection);
    if (!changed) {
      return selectedRunByIdRef.current;
    }

    const next = { ...cacheRef.current };
    selectedRunByIdRef.current = next;
    return next;
  }, [runs, rowSelection]);

  useEffect(() => {
    if (rowSelectionCount === 0 && isSelectedOnly) {
      deselectAll();
    }
  }, [rowSelectionCount, isSelectedOnly, deselectAll]);

  return (
    <div className="w-full pt-12">
      <p className="body-medium text-sys-brand-on-surface-variant">
        Review prior finalize-election batches, including who initiated each run, how many applications were submitted,
        and the overall outcome.
      </p>
      <PastMemberElectionRunsTable
        loading={loading}
        runs={runs}
        selectedRunById={selectedRunById}
        showLoadError={showLoadError}
        bulkSelection={bulkSelection}
        createHeaderPagination={createHeaderPagination}
        createFooterPagination={createFooterPagination}
      />
    </div>
  );
}
